import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

const STORAGE_KEY = 'nubesmty_pwa_dismissed_v1';

// Registers the service worker and shows a minimal install banner
// when the browser fires `beforeinstallprompt` (Chrome/Edge/Android).
// iOS Safari doesn't fire that event — we render a lightweight hint
// only when in Safari and not standalone.
const PWAInstall = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    // Register the service worker (production only; CRA dev doesn't like SW)
    if ('serviceWorker' in navigator && window.location.protocol === 'https:') {
      const registerSW = () => {
        navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {});
      };
      // Register immediately if page already loaded, otherwise wait for load
      if (document.readyState === 'complete') {
        registerSW();
      } else {
        window.addEventListener('load', registerSW, { once: true });
      }

      // When a push arrives while the app is open, the SW postMessages us — play sound.
      const onMsg = (event) => {
        const data = event.data || {};
        if (data.type === 'nubesmty-push') {
          try {
            const a = new Audio((data.payload && data.payload.sound) || '/sounds/notify.wav');
            a.volume = 0.55;
            a.play().catch(() => {}); // user gesture may block; OS notification still fires
          } catch {}
        }
        if (data.type === 'nubesmty-navigate' && data.url) {
          try { window.location.href = data.url; } catch {}
        }
      };
      navigator.serviceWorker.addEventListener('message', onMsg);
    }

    const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) return;

    const dismissed = (() => {
      try { return Number(localStorage.getItem(STORAGE_KEY)) || 0; } catch { return 0; }
    })();
    // Respect "not now" for 7 days
    if (dismissed && Date.now() - dismissed < 7 * 24 * 60 * 60 * 1000) return;

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Show after 5s so it doesn't interrupt landing UX
      setTimeout(() => setVisible(true), 5000);
    };
    window.addEventListener('beforeinstallprompt', onBIP);

    // iOS Safari detection
    const ua = window.navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    if (isIOS && isSafari) {
      setTimeout(() => setIosHint(true), 6000);
    }

    return () => window.removeEventListener('beforeinstallprompt', onBIP);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
    setVisible(false);
    setIosHint(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch {}
    setDeferred(null);
    setVisible(false);
  };

  if (!visible && !iosHint) return null;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-24 md:bottom-6 z-[60] w-[92%] max-w-md rounded-2xl border border-orange-500/25 bg-neutral-950/95 backdrop-blur-md shadow-2xl shadow-black/60 px-4 py-3.5 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-4 duration-500"
      role="dialog"
      aria-label="Instalar aplicación"
      data-testid="pwa-install-banner"
    >
      <div className="w-11 h-11 shrink-0 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/25">
        <Download className="w-5 h-5 text-black" strokeWidth={2.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-[13px] font-bold leading-tight">Instala NubesMTY en tu celular</p>
        <p className="text-white/60 text-[11px] mt-0.5 leading-snug">
          {iosHint && !deferred
            ? <>Toca <span className="text-orange-300 font-bold">Compartir</span> y luego <span className="text-orange-300 font-bold">"Agregar a inicio"</span>.</>
            : 'Accede más rápido y recibe pedidos con un tap.'}
        </p>
      </div>
      {deferred ? (
        <button
          onClick={install}
          className="shrink-0 h-9 px-4 rounded-full bg-orange-500 hover:bg-orange-600 text-black text-xs font-black uppercase tracking-wide"
          data-testid="pwa-install-btn"
        >Instalar</button>
      ) : null}
      <button
        onClick={dismiss}
        className="shrink-0 w-8 h-8 rounded-full hover:bg-white/10 text-white/60 hover:text-white flex items-center justify-center"
        aria-label="Cerrar"
        data-testid="pwa-install-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default PWAInstall;
